/**
 * Chat lifecycle — frontend entry point.
 *
 * WHY THIS EXISTS
 * ---------------
 * "Is this chat still open?" was answered independently at every call site, and the
 * answers drifted apart. On 2026-08-12 the AC badge reported 14 open chats while the
 * visible workspace tabs summed to 13. Both numbers were computed correctly — from
 * two different rules:
 *
 *   · AC counted by Rule 1  (open until Finish is clicked)          → 14
 *   · tab visibility hung off the attention pass, which applies
 *     Rule 3 and skips liveness==='stuck'                           → 13
 *
 * The missing chat sat in the "Arch Worker" workspace, which therefore had no tab at
 * all — an open chat with no way to reach it. Nobody wrote a bug; two reasonable local
 * decisions simply disagreed, and nothing in the code forced them to agree.
 *
 * WHERE THE RULES LIVE NOW
 * ------------------------
 * In ../../shared/chat-lifecycle — one definition for BOTH tiers. This file could only
 * ever bind the frontend; the server answered the same question in its own route
 * handlers, so it kept its own copies and the same drift happened again. By 2026-08-17
 * the rule was re-implemented inline in eight places, and LayoutManager had gone back to
 * deciding EXISTENCE with Rule 3 — the exact hiding forbidden below.
 *
 * Read that file's header for the rules and their reasoning. The re-export above keeps
 * every existing frontend import pointing at that one definition; Rule 2 (sub-session
 * detection) still lives in ./subSession — same idea, same reason.
 *
 * An orphan is STILL OPEN. It stays visible and keeps its tab; it just must not paint
 * that tab orange forever, which is what the 2026-08-04 attention skip was for. Hiding
 * an orphan is not allowed: "a workspace with an unfinished chat must be open"
 * (Rafael, 2026-08-12). Hiding is what produced the mismatch above.
 *
 * What stays in this file is what is genuinely frontend-only: workspace attribution and
 * the loud report when it fails.
 */

export type { LifecycleConv } from '../../shared/chat-lifecycle';
export { isOpenChat, isOrphanedChat } from '../../shared/chat-lifecycle';

import type { LifecycleConv } from '../../shared/chat-lifecycle';

/**
 * Attribute a conversation to a workspace.
 *
 * Prefer the explicit projectId tag; fall back to an EXACT display-name match for
 * legacy/untagged sessions. Never a substring match — on 2026-08-01 that let
 * "werking-energy" match "werking-energy-report" and colour the wrong tab.
 *
 * Returns null when the conversation cannot be attributed. That case is EXPECTED and
 * already surfaced to the user: the server tags such sessions UNASSIGNED_PROJECT_NAME
 * and AC groups them under "Kein Workspace". So the per-project buckets legitimately
 * do not sum to the AC total — the difference is exactly that group.
 *
 * What callers must not do is drop them without noticing (see
 * reportUnattributableOpenChats): the buckets not summing is only acceptable while the
 * remainder is visible somewhere. If that ever stops being true, the console line is
 * what tells you.
 */
export function resolveProjectId(
  conv: LifecycleConv,
  validIds: Set<string>,
  nameToId: Map<string, string>,
): string | null {
  if (conv.projectId && validIds.has(conv.projectId)) return conv.projectId;
  const byName = nameToId.get((conv.projectName || '').toLowerCase());
  return byName ?? null;
}

/**
 * Fail loud when open chats cannot be attributed to any workspace.
 *
 * Deliberately does NOT throw. This runs inside a 5s polling effect that renders the
 * whole shell; throwing would blank the UI over a counting inconsistency and take the
 * user's chats with it. A UI has no operator standing by — recovery has to be
 * architectural, not a rescue click (same reasoning as the polling-backoff refactor
 * 1a7946473). So "fail fast" here means: never silently divergent, always named in the
 * console with the sessions that caused it, and reflected in the returned count so the
 * caller can surface it instead of quietly showing a total that does not add up.
 */
export function reportUnattributableOpenChats(convs: LifecycleConv[]): LifecycleConv[] {
  if (convs.length === 0) return convs;
  console.warn(
    `[chatLifecycle] ${convs.length} open chat(s) belong to no known workspace, so the ` +
    `per-tab numbers cannot sum to the AC total. Expected only for sessions AC shows ` +
    `under "Kein Workspace" — if one is missing there too, it is unreachable.`,
    convs.map(c => ({
      sessionId: c.sessionId,
      projectId: c.projectId,
      projectName: c.projectName,
    })),
  );
  return convs;
}
